// ** Import from react dom
import { Route, Routes, Navigate } from 'react-router-dom';

// ** Import core SCSS styles
import 'assets/scss/theme.scss';

// Import Auth
import RequireAuth from '@auth-kit/react-router/RequireAuth'; // Used to Secure individual routes one at a time, preferred for a granular approach.
import AuthOutlet from '@auth-kit/react-router/AuthOutlet'; // Used to Secure multiple routes at once.
// Problem mit <Navigate>, wenn AuthOutlet verwendet wird, wird es nicht erkannt

// ** Import Layouts
import AuthLayout from 'layouts/dashboard/AuthLayout';
import HelpCenterLayout from 'layouts/marketing/HelpCenterLayout'; // ( added in v1.3.0 )
import DashboardIndex from 'layouts/dashboard/DashboardIndex'; // ( added in v1.3.0 )
import DashboardIndexCompact from 'layouts/dashboard/DashboardIndexCompact'; // ( added in v1.3.0 )
import HelpCenterTransparentLayout from 'layouts/marketing/HelpCenterTransparentLayout'; // ( added in v2.0.0 )

// ** Import Dahbaord Menu Pages
import Overview from 'components/dashboard/overview/Overview';

// ** Import Authentication components
import SignIn from 'components/dashboard/authentication/SignIn';
import SignUp from 'components/dashboard/authentication/SignUp';
import ForgetPassword from 'components/dashboard/authentication/ForgetPassword';
import OTP from 'components/dashboard/authentication/OTP';
import AmazonCallback from 'components/dashboard/authentication/amazon/AmazonCallback';

// ** Import Administration components
import AllAdminConnectorSettings from 'components/dashboard/administration/connector/AdminConnectorSettings';
import AllAdminCronSettings from 'components/dashboard/administration/cron/AdminCronSettings';
import AllAdminEmailSettings from 'components/dashboard/administration/email/AdminEmailSettings';
import AllAdminMarketingSettings from 'components/dashboard/administration/marketing/AdminMarketingSettings';
import AllAdminLogs from 'components/dashboard/administration/logs/AdminLogs';

// ** Import Settings components
import AllSettings from 'components/dashboard/settings/Settings';

// ** Import Profile components
import AllProfile from 'components/dashboard/profile/Profile';

// ** Flows -> Flow Pages
import Returns from 'components/dashboard/flows/Returns';

// ** Marketplace
import AllMarketplaceFlows from 'components/dashboard/marketplace/flows/AllMarketplaceFlows';

// ** Marketplace -> Flow Configuration Pages
import FlowConfigurationSingle from 'components/dashboard/marketplace/flows/single/overview/FlowConfigurationSingle';

// ** Import docs and change log pages
import ChangeLog from 'components/dashboard/changelog/ChangeLog';

// ** Import notification component
import Notifications from 'components/dashboard/authentication/Notifications';

/* ----------------------------------- */
/* IMPORTS FOR MARKETING PAGES - START */
import DefaultLayout from 'layouts/marketing/DefaultLayout';
import BlankLayout from 'layouts/marketing/BlankLayout';
import NotFound from 'layouts/marketing/NotFound';

/* IMPORTS FOR FRONT SIDE PAGES MENU */
import Imprint from 'components/marketing/pages/about/Imprint';
import Privacy from 'components/marketing/pages/about/Privacy';
import TermsAndConditions from 'components/marketing/pages/about/TermsAndConditions';
import ComparePlan from 'components/marketing/pages/compare-plan/ComparePlan'; // new v1.1.0
import Checkout from 'components/marketing/pages/checkout/Checkout';
import Contact from 'components/marketing/pages/contact/Contact'; // new v1.1.0

// IMPORTS FOR HELP CENTER PAGES ( v1.3.0 )
import HelpCenter from 'components/marketing/pages/help-center/help-center/HelpCenter';
import HelpCenterFAQ from 'components/marketing/pages/help-center/help-center-faq/HelpCenterFAQ';
import HelpCenterGuide from 'components/marketing/pages/help-center/help-center-guide/HelpCenterGuide';
import HelpCenterGuideCategory from 'components/marketing/pages/help-center/help-center-guide-category/HelpCenterGuideCategory';
import HelpCenterGuideSingle from 'components/marketing/pages/help-center/help-center-guide-single/HelpCenterGuideSingle';
import HelpCenterSupport from 'components/marketing/pages/help-center/help-center-support/HelpCenterSupport';
import HelpCenterStatuspage from 'components/marketing/pages/help-center/help-center-statuspage/HelpCenterStatusPage';

/* IMPORTS FOR FRONT SPECIALTY SUBMENU  ROUTERS */
import Error404 from 'components/marketing/pages/specialty/Error404';

// Landing menu item pages
import LandingHaeppyapp from 'components/marketing/landings/landing-haeppyapp/LandingHaeppyapp';

// Import withRole HOC
import withRole from 'components/hoc/withRole';

const AllRoutes = () => {
	return (
		<Routes>
			{/* Routes with DefaultLayout */}
			<Route element={<DefaultLayout />}>
				<Route path="/" element={<LandingHaeppyapp />} />
				<Route
					path="/marketing/about/imprint/"
					element={<Imprint />}
				/>
				<Route
					path="/marketing/about/privacy/"
					element={<Privacy />}
				/>
				<Route
					path="/marketing/about/terms-and-conditions/"
					element={<TermsAndConditions />}
				/>
				<Route 
					path="/marketing/pages/compare-plan/" 
					element={<ComparePlan />} 
				/>
			</Route>

			{/* Routes with BlankLayout */}
			<Route element={<BlankLayout />}>
				<Route path="/marketing/pages/contact/" element={<Contact />} />
				<Route path="/marketing/pages/checkout" element={<Checkout />} />

				<Route
					path="/marketing/specialty/terms-and-conditions/"
					element={<TermsAndConditions />}
				/>
			</Route>

			{/* Routes with NotFound */}
			<Route element={<NotFound />}>
				<Route path="/marketing/specialty/404-error/" element={<Error404 />} />
			</Route>

			{/* Routes with HelpCenterTransparentLayout */}
			<Route element={<HelpCenterTransparentLayout />}>
				<Route path="/marketing/help-center/" element={<HelpCenter />} />
			</Route>

			{/* Routes with HelpCenterLayout */}
			<Route element={<HelpCenterLayout />}>
				<Route path="/marketing/help-center/faq/" element={<HelpCenterFAQ />} />
				<Route
					path="/marketing/help-center/guide/"
					element={<HelpCenterGuide />}
				/>
				<Route
					path="/marketing/help-center/guide-single/:categoryslug/:articleslug"
					element={<HelpCenterGuideSingle />}
				/>
				<Route
					path="/marketing/help-center/guide-single/:categoryslug"
					element={<HelpCenterGuideCategory />}
				/>
				<Route
					path="/marketing/help-center/support/"
					element={<HelpCenterSupport />}
				/>
				<Route
					path="/marketing/help-center/statuspage/"
					element={<HelpCenterStatuspage />}
				/>
			</Route>

			{/* Routes with AuthLayout */}
			<Route element={<AuthLayout />}>
				<Route path="/authentication/sign-in" element={<SignIn />} />
				<Route path="/authentication/sign-up" element={<SignUp />} />
				<Route path="/authentication/forget-password" element={<ForgetPassword />} />
				<Route path="/authentication/otp" element={<OTP />} />
				<Route path="/authentication/amazon/callback" element={<AmazonCallback />} />
			</Route>

			{/* Routes (DASHBOARD ROUTERS) with DashboardIndex */}
			<Route element={<DashboardIndex />}>
				<Route path="/dashboard/overview" element={<Overview />} />
				<Route path="/authentication/notifications" element={<Notifications />} />

				{/* ADMINISTRATION ROUTERS */}
				<Route path="/administration/connector/settings" element={<AllAdminConnectorSettings />} />
				<Route path="/administration/cron/settings" element={<AllAdminCronSettings />} />
				<Route path="/administration/email/settings" element={<AllAdminEmailSettings />} />
				<Route path="/administration/marketing/settings" element={<AllAdminMarketingSettings />} />
				<Route path="/administration/logs" element={<AllAdminLogs />} />

				{/* SETTINGS ROUTERS */}
				<Route path="/settings/general" element={<AllSettings />} />

				{/* PROFILE ROUTERS */}
				<Route path="/profile/general" element={<AllProfile />} />

				{/* FLOWS */}
				<Route path="/dashboard/flows/returns" element={<Returns />}/>

				{/* MARKETPLACE */}
				<Route path="/dashboard/marketplace/flows" element={<AllMarketplaceFlows />} />

				{/* FLOW CONFIGURATION */}
				<Route path="/dashboard/marketplace/flows/:id" element={<FlowConfigurationSingle />} />

				<Route path="/administration/cron/settings" element={<AllAdminCronSettings />} />
				<Route path="/administration/email/settings" element={<AllAdminEmailSettings />} />
				<Route path="/administration/logs" element={<AllAdminLogs />} />

				{/* SETTINGS ROUTERS */}
				<Route path="/settings/general" element={<AllSettings />} />

				{/* PROFILE ROUTERS */}
				<Route path="/profile/general" element={<AllProfile />} />

				{/* FLOWS */}
				<Route path="/dashboard/flows/returns" element={<Returns />}/>

				{/* CHANGELOG */}
				<Route path="/dashboard/changelog" element={<ChangeLog />} />

			</Route>

			{/*Redirect*/}
			<Route
				path="*"
				element={<Navigate to="/marketing/specialty/404-error/" replace />}
			/>
		</Routes>
	);
};

export default AllRoutes;
